// app/frontend/data/faqData.tsx
import React from 'react';
import { Link } from '@inertiajs/react';
import { Text, Anchor } from '@mantine/core';

export interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
}

export interface FAQSection {
  section: string;
  items: FAQItem[];
}

export const faqData: FAQSection[] = [
  {
    section: 'General',
    items: [
      {
        question: 'What is the goal of this site?',
        answer: (
          <>
            <Text className="mb-2">
              The goal of this site is to provide a central location for people to find and submit events and artists
              all in one freaking place!
            </Text>
            <Text className="mb-2">
              From local weekly classes, practicas, retreats, marathons, festivals, and everything in between,{' '}
              <Text component="span" td="underline">
                {' '}
                we want to provide a place for people to find all of the events{' '}
              </Text>{' '}
              that are happening in their area and{' '}
              <Text component="span" fw={700}>
                all around the world.
              </Text>{' '}
            </Text>
          </>
        ),
      },
    ],
  },
  {
    section: 'Events',
    items: [
      {
        question: 'Do I have to be the organizer to add an event?',
        answer: (
          <>
            <Text className="mb-2">
              No, anyone can add an event. Whether you are the organizer or just a community member, you can add an
              event to our site. You can do so by clicking {'  '}
              <Anchor
                component={Link}
                href="/suggest_event"
                c="
            orange"
                fw={700}
              >
                Add A New Event
              </Anchor>
              .
            </Text>
            <Text className="mb-2">
              If you are the organizer, we encourage you to create an account so you can manage your events.
            </Text>
          </>
        ),
      },
      {
        question: "I can't find an event that I know exists, how can I submit it?",
        answer: (
          <Text>
            After you create a user account, you can submit an event by clicking the{' '}
            <Anchor
              component={Link}
              href="/suggest_event"
              c="
            darkorange"
              fw={700}
            >
              Add A New Event
            </Anchor>{' '}
            button menu.
          </Text>
        ),
      },
      {
        question: 'Is there a quick way to duplicate an existing event?',
        answer:
          'Yes! Once you are logged in at the top of the event page you will see a manage button. Click that and then scroll the menu at the top to the right until you see the More tab. Click that and you will see a button to duplicate the event.',
      },
    ],
  },
  {
    section: 'Artists',
    items: [
      {
        question: 'Who can add an artist to WCWD?',
        answer: (
          <>
            <Text className="mb-2">
              Anyone can add any artist to the site if you'd like for them to have an artist profile.
            </Text>
            <Text className="mb-2">
              You can add a new artist by clicking{' '}
              <Anchor component={Link} href="/suggest_artist" c="darkorange" fw={700}>
                add a new artist
              </Anchor>
              .
            </Text>
            <Text className="mb-2">
              If you see your artist profile on our site and you want to claim it so that you can update your image,
              bio, social media links, etc. Please message our{' '}
              <Anchor href="https://www.facebook.com/wherecanwedance" target="_blank" fw={700} c="darkorange">
                Facebook page
              </Anchor>{' '}
              to start that process.
            </Text>
          </>
        ),
      },
      {
        question: 'Why should I create an artist profile?',
        answer: (
          <>
            <Text className="mb-2">
              Creating an artist profile gives you the opportunity to create a comprehensive online presence on our
              website. Your artist profile will list all of your upcoming events, which can be added by you or by the
              event organizers who hire you.
            </Text>
            <Text className="mb-2">
              This profile also serves as a virtual resume, showcasing all of the previous events you have participated
              in.
            </Text>
            <Text className="mb-2">
              We have noticed that most artists do not their own website and don't have a central place when their
              followers can view their upcoming travel dates all in one place.
            </Text>
            <Text className="mb-2">
              You can add a new artist by clicking{' '}
              <Anchor component={Link} href="/suggest_artist" c="darkorange" fw={700}>
                add a new artist
              </Anchor>
              .
            </Text>
          </>
        ),
      },
      {
        question: 'What are the steps to add an artist on WCWD?',
        answer: (
          <>
            <Text className="mb-2">
              <strong>Step 1:</strong> Create a free user account{' '}
              <Anchor href="http://www.wherecanwedance.com/register" target="_blank" c="orange" fw={700}>
                here
              </Anchor>
              . Be sure to confirmation your account with the confirmation email that will be sent to you.
            </Text>
            <Text className="mb-2">
              <strong>Step 2:</strong> Log in to your account.
            </Text>
            <Text className="mb-2">
              <strong>Step 3:</strong> Navigate to Artist in the menu and click the "Add Artist" button.
            </Text>
            <Text className="mb-2">
              <strong>Step 4:</strong> Fill out the form and click "Create Artist".
            </Text>
            <Text>
              <strong>Step 5:</strong> You should receive an email confirming that you have submitted an artist. From
              here the artist will be approved within 48 hours. Once approved the aritst will be added to our directory
              of artists!
            </Text>
          </>
        ),
      },
      {
        question: 'How do I edit my artist profile?',
        answer: (
          <Text>
            If you see that you already have an artist profile listed, we invite you to create a free account so you can
            manage your own artist profile. Once your account is created message our{' '}
            <Anchor href="https://www.facebook.com/wherecanwedance" target="_blank" c="orange" fw={700}>
              Facebook page
            </Anchor>
            .
          </Text>
        ),
      },
      {
        question: 'How do I add events to an artist profile?',
        answer: (
          <>
            <Text className="mb-2">There are two ways to add events to an artist profile.</Text>
            <Text className="mb-2">
              The first is through editing the artist profile and the second is through editing the event itself.
            </Text>
            <Text className="mb-2">
              When you edit the artist profile{' '}
              <u>(you can only edit aritsts that you have added to the site or have access to edit)</u>, there a section
              for booked events where you can search the current list of events on our site and add them to the artist
              profile.
            </Text>
            <Text className="mb-2">
              When you edit an event
              <u>(you can only edit events that you have added to the site or have access to edit)</u>, there is a
              section for artists where you can search the current list of artists on our site and add them to the
              event.
            </Text>
            <Text>
              Once you add an event to an artist profile or an artist to an event, the event will automatically be added
              to the artist profile and the artist will automatically be added to the event under Artists.
            </Text>
          </>
        ),
      },
    ],
  },
  {
    section: 'Cities',
    items: [
      {
        question: "I don't see my city listed, how can I add it?",
        answer: (
          <>
            <Text className="mb-2">
              The current list of cities is based on the events that have been added to the site. If you don't see your
              city listed, you can first add an event with an address in the city you want to add.
            </Text>
            <Text className="mb-2">
              From here message our{' '}
              <Anchor href="https://www.facebook.com/wherecanwedance" target="_blank" c="orange" fw={700}>
                Facebook page
              </Anchor>{' '}
              to request that we add your city to the list of cities. We typically like to have at least 5 upcoming
              events in each city.
            </Text>
            <Text className="mb-2">
              If you are an organizer, artist, or dancer in that city, we encourage you to add events to the site so
              that we can grow the list of events in your city.
            </Text>
          </>
        ),
      },
    ],
  },
];

// Helper function to easily add new FAQ items
export const addFAQItem = (sectionName: string, question: string, answer: string | React.ReactNode) => {
  // This is just an example of how you could programmatically add items
  // In practice, you'd just edit the faqData array above
  console.log(`Adding FAQ item to ${sectionName}: ${question}`);
};
